import { useState, useMemo } from 'react';
import { Checkbox, Input, Empty, Button, Tooltip, Badge } from 'antd';
import {
  SearchOutlined,
  UnlockOutlined,
  CheckOutlined,
  ClearOutlined,
  DownOutlined,
  InfoCircleOutlined,
  SelectOutlined, FolderOpenOutlined,
  FolderOutlined
} from '@ant-design/icons';
import '../../assets/css/permissions-table-v3.css';

const PermissionsTable = ({ data, value = [], OnChange, error, touched }) => {
  const [expandedModules, setExpandedModules] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract unique modules and counts
  const modules = useMemo(() => {
    const moduleMap = {};
    data.forEach(item => {
      const modName = item.moduleName || 'Other';
      if (!moduleMap[modName]) {
        moduleMap[modName] = {
          name: modName,
          total: 0,
          selected: 0,
          permissions: []
        };
      }
      moduleMap[modName].total += 1;
      moduleMap[modName].permissions.push(item);
      if (value.includes(item.name)) {
        moduleMap[modName].selected += 1;
      }
    });

    return Object.values(moduleMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, value]);

  // Filtered view for search
  const searchResults = useMemo(() => {
    if (!searchQuery) return null;
    const query = searchQuery.toLowerCase();
    return data.filter(item =>
      item.name.toLowerCase().includes(query) ||
      (item.description && item.description.toLowerCase().includes(query)) ||
      item.moduleName.toLowerCase().includes(query)
    );
  }, [data, searchQuery]);

  const toggleModule = (moduleName) => {
    setExpandedModules(prev =>
      prev.includes(moduleName)
        ? prev.filter(m => m !== moduleName)
        : [...prev, moduleName]
    );
  };

  const expandAll = () => setExpandedModules(modules.map(m => m.name));
  const collapseAll = () => setExpandedModules([]);

  const togglePermission = (permName) => {
    const newValue = value.includes(permName)
      ? value.filter(v => v !== permName)
      : [...value, permName];
    OnChange(newValue);
  };

  const handleSelectAllModule = (e, modulePerms) => {
    e.stopPropagation();
    const permNames = modulePerms.map(p => p.name);
    const allSelected = permNames.every(p => value.includes(p));

    let newValue;
    if (allSelected) {
      newValue = value.filter(p => !permNames.includes(p));
    } else {
      const otherPerms = value.filter(p => !permNames.includes(p));
      newValue = [...otherPerms, ...permNames];
    }
    OnChange(newValue);
  };

  const clearAllSelections = () => OnChange([]);

  return (
    <div className={`permissions-v3-container ${error && touched ? 'p-error-wrapper' : ''}`}>
      {/* Search & Global Controls */}
      <div className="p-controls">
        <div className="p-header-row">
          <div className="p-title">
            <h3>Role Permissions</h3>
          </div>
          <div className="p-global-actions">
            {!searchQuery && (
              <>
                <Button size="small" type="text" icon={<FolderOpenOutlined />} onClick={expandAll}>
                  Expand All
                </Button>
                <Button size="small" type="text" icon={<FolderOutlined />} onClick={collapseAll}>
                  Collapse All
                </Button>
              </>
            )}
            <Button size="small" type="text" danger icon={<ClearOutlined />} onClick={clearAllSelections}>
              Clear All
            </Button>
          </div>
        </div>
        <div className="p-search-wrapper">
          <Input
            placeholder="Search by module, name or description..."
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size="large"
            allowClear
            style={{ borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}
          />
        </div>
      </div>

      {/* Module List (Collapsible) */}
      <div className="p-modules-wrapper">
        {searchQuery ? (
          <div className="p-search-results">
            {searchResults.length > 0 ? (
              <div className="p-grid">
                {searchResults.map(perm => {
                  const isSelected = value.includes(perm.name);
                  return (
                    <div
                      key={perm.name}
                      className={`p-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => togglePermission(perm.name)}
                    >
                      <Checkbox checked={isSelected} />
                      <div className="p-card-text">
                        <span className="p-card-label">
                          {perm.name.split('.').pop().toUpperCase()}
                          <small style={{ display: 'block', color: '#94a3b8', fontSize: '10px', fontWeight: 'normal' }}>
                            {perm.moduleName}
                          </small>
                        </span>
                        <span className="p-card-desc">{perm.description || `Manage ${perm.name.replace(/\./g, ' ')}`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description="No permissions matching your criteria" />
            )}
          </div>
        ) : (
          modules.map(module => {
            const isExpanded = expandedModules.includes(module.name);
            const isAllSelected = module.selected === module.total;
            const isSomeSelected = module.selected > 0;

            return (
              <div key={module.name} className={`p-module-section ${isExpanded ? 'expanded' : ''}`}>
                {/* Module Header */}
                <div className="p-module-header" onClick={() => toggleModule(module.name)}>
                  <div className="p-module-info">
                    <div className="p-module-icon">
                      <UnlockOutlined />
                    </div>
                    <span className="p-module-name">{module.name.replace(/-/g, ' ')}</span>
                    <div className="p-module-count">
                      <span className="p-count-selected">{module.selected}</span>
                      <span>/</span>
                      <span>{module.total}</span>
                    </div>
                    {isAllSelected && <Badge status="success" style={{ marginLeft: -8 }} />}
                  </div>
                  <div className="p-module-actions">
                    <Button
                      size="small"
                      type={isAllSelected ? "primary" : "default"}
                      ghost={!isAllSelected}
                      onClick={(e) => handleSelectAllModule(e, module.permissions)}
                      icon={isAllSelected ? <CheckOutlined /> : <SelectOutlined />}
                      style={{ borderRadius: '6px', fontSize: '12px', height: '28px' }}
                    >
                      {isAllSelected ? 'Module Selected' : 'Select Module'}
                    </Button>
                    <DownOutlined className="p-chevron" />
                  </div>
                </div>

                {/* Module Content */}
                {isExpanded && (
                  <div className="p-module-content">
                    <div className="p-grid">
                      {module.permissions.map(perm => {
                        const isItemSelected = value.includes(perm.name);
                        return (
                          <div
                            key={perm.name}
                            className={`p-card ${isItemSelected ? 'selected' : ''}`}
                            onClick={() => togglePermission(perm.name)}
                          >
                            <Checkbox checked={isItemSelected} />
                            <div className="p-card-text">
                              <span className="p-card-label">
                                {perm.name.split('.').pop().toUpperCase()}
                              </span>
                              <Tooltip title={perm.description}>
                                <span className="p-card-desc">
                                  {perm.description || `Access to ${perm.name.replace(/\./g, ' ')}`}
                                </span>
                              </Tooltip>
                            </div>
                            <Tooltip title={perm.name}>
                              <InfoCircleOutlined style={{ fontSize: '12px', color: '#cbd5e1' }} />
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {error && touched && <div className="p-error-text">{error}</div>}
    </div>
  );
};

export default PermissionsTable;